
import { BoundingBox, XYPos } from "../Common/CommonTypes.js";
import { Union, Record } from "../fable_modules/fable-library-js.5.13.0/Types.js";
import { tuple_type, class_type, array_type, union_type, int32_type, list_type, option_type, string_type, record_type, float64_type } from "../fable_modules/fable-library-js.5.13.0/Reflection.js";
import { DrawModelType_BusWireT_Wire, DrawModelType_BusWireT_Segment, DrawModelType_BusWireT_Wire_$reflection, DrawModelType_BusWireT_ASegment_$reflection } from "../Model/DrawModelType.js";
import { updateAt, map, length, item } from "../fable_modules/fable-library-js.5.13.0/List.js";
import { interpolate, printf, toFail, join, toText } from "../fable_modules/fable-library-js.5.13.0/String.js";
import { Constants_intervalTolerance, getAbsSegments } from "./BlockHelpers.js";
import { value as value_2 } from "../fable_modules/fable-library-js.5.13.0/Option.js";
import { item as item_1, map as map_1 } from "../fable_modules/fable-library-js.5.13.0/Array.js";
import { max, min } from "../fable_modules/fable-library-js.5.13.0/Double.js";
import { change } from "../fable_modules/fable-library-js.5.13.0/Map.js";

export const Constants_wireSeparationFromSymbol = 15;

export const Constants_maxCallsToShiftHorizontalSeg = 5;

export const Constants_minVisibleSegmentLength = 0.5;

export const Constants_minWireSeparation = 7;

export const Constants_smallOffset = 0.0001;

export const Constants_maxSegmentSeparation = 30;

export const Constants_overlapTolerance = 2;

export const Constants_separateCaptureOverlap = 35;

export const Constants_minWireLengthToSeparate = 10;

export const Constants_sameNetTrunkCapture = 10;

export const Constants_maxCornerSize = 100;

export const Constants_overlapCostWeight = 20;

export const Constants_clearanceFromSymbol = Constants_maxSegmentSeparation;

export const Constants_clearanceCostWeight = 5;

export const Constants_maxSettlingRounds = 2;

export const Constants_settlingTolerance = 1;

export const Constants_extensionTolerance = 3;

/**
 * swap X and Y coordinates if orientation = Vertical
 */
export function swapXYWhenVertical(pos, orientation) {
    if (orientation === "vertical") {
        return new XYPos(pos.Y, pos.X);
    }
    else {
        return pos;
    }
}

/**
 * swap X & Y coordinats in BB if orientation is vertical
 */
export function swapBBXAndY(box, orientation) {
    if (orientation === "vertical") {
        return new BoundingBox(swapXYWhenVertical(box.TopLeft, orientation), box.H, box.W);
    }
    else {
        return box;
    }
}

/**
 * Return new poistion moved the the direction and amount shown.
 */
export function updatePos(direction, distanceToShift, pos) {
    switch (direction) {
        case "down_":
            return new XYPos(pos.X, pos.Y + distanceToShift);
        case "left_":
            return new XYPos(pos.X - distanceToShift, pos.Y);
        case "right_":
            return new XYPos(pos.X + distanceToShift, pos.Y);
        default:
            return new XYPos(pos.X, pos.Y - distanceToShift);
    }
}

/**
 * Used to capture the 1D coordinates of the two ends of a line. (see Line).
 */
export class Bound extends Record {
    constructor(MinB, MaxB) {
        super();
        this.MinB = MinB;
        this.MaxB = MaxB;
    }
}

export function Bound_$reflection() {
    return record_type("BusWireRoutingHelpers.Bound", [], Bound, () => [["MinB", float64_type], ["MaxB", float64_type]]);
}

export function LineId__get_Index(this$) {
    return this$;
}

/**
 * Used to represent a line on the canvas, e.g. a wire segment or symbol edge.
 * The array of lines will all have the same orientation - so optimisation is done in two phases
 * for vertical and horizontal segments. Each phase optimises one half of the segments.
 */
export class Line extends Record {
    constructor(P, B, Orientation, Seg1, LType, SameNetLink, Wid, PortId, Lid) {
        super();
        this.P = P;
        this.B = B;
        this.Orientation = Orientation;
        this.Seg1 = Seg1;
        this.LType = LType;
        this.SameNetLink = SameNetLink;
        this.Wid = Wid;
        this.PortId = PortId;
        this.Lid = Lid;
    }
}

export function Line_$reflection() {
    return record_type("BusWireRoutingHelpers.Line", [], Line, () => [["P", float64_type], ["B", Bound_$reflection()], ["Orientation", string_type], ["Seg1", option_type(DrawModelType_BusWireT_ASegment_$reflection())], ["LType", string_type], ["SameNetLink", list_type(Line_$reflection())], ["Wid", int32_type], ["PortId", int32_type], ["Lid", int32_type]]);
}

/**
 * Used to cluster together overlapping and adjacent lines into a group that
 * can be spread out. This is the parameter in a tail recursion used to do the clustering
 */
export class Cluster extends Record {
    constructor(UpperFix, LowerFix, Segments, Bound) {
        super();
        this.UpperFix = UpperFix;
        this.LowerFix = LowerFix;
        this.Segments = Segments;
        this.Bound = Bound;
    }
}

export function Cluster_$reflection() {
    return record_type("BusWireRoutingHelpers.Cluster", [], Cluster, () => [["UpperFix", option_type(float64_type)], ["LowerFix", option_type(float64_type)], ["Segments", list_type(int32_type)], ["Bound", Bound_$reflection()]]);
}

export const clusterSegments_ = [(c) => c.Segments, (n) => ((c_1) => (new Cluster(c_1.UpperFix, c_1.LowerFix, n, c_1.Bound)))];

/**
 * Controls direction of Cluster search in expandCluster.
 * Search is upwards first and then downwards so downwards search takes a Cluster
 * (generated from upwards search) as parameter.
 */
export class LocSearchDir extends Union {
    constructor(tag, fields) {
        super();
        this.tag = tag;
        this.fields = fields;
    }
    cases() {
        return ["Upwards", "Downwards"];
    }
    static Upwards = new LocSearchDir(0, []);
}

export function LocSearchDir_$reflection() {
    return union_type("BusWireRoutingHelpers.LocSearchDir", [], LocSearchDir, () => [[], [["Item", Cluster_$reflection()]]]);
}

export class Extension extends Record {
    constructor(ExtOri, ExtB, ExtP) {
        super();
        this.ExtOri = ExtOri;
        this.ExtB = ExtB;
        this.ExtP = ExtP;
    }
}

export function Extension_$reflection() {
    return record_type("BusWireRoutingHelpers.Extension", [], Extension, () => [["ExtOri", string_type], ["ExtB", Bound_$reflection()], ["ExtP", float64_type]]);
}

/**
 * Defines a wire corner that could be removed.
 * the two removed segments are [StartSeg+1..EndSeg-1]
 * In addition, StartSeg and EndSeg have Length changed.
 */
export class WireCorner extends Record {
    constructor(Wire, StartSeg, StartSegChange, EndSegChange, StartSegOrientation) {
        super();
        this.Wire = Wire;
        this.StartSeg = (StartSeg | 0);
        this.StartSegChange = StartSegChange;
        this.EndSegChange = EndSegChange;
        this.StartSegOrientation = StartSegOrientation;
    }
}

export function WireCorner_$reflection() {
    return record_type("BusWireRoutingHelpers.WireCorner", [], WireCorner, () => [["Wire", DrawModelType_BusWireT_Wire_$reflection()], ["StartSeg", int32_type], ["StartSegChange", float64_type], ["EndSegChange", float64_type], ["StartSegOrientation", string_type]]);
}

/**
 * Some operations need to work on Horizontal and Vertical lines together.
 * This captures the static information needed to do this.
 */
export class LineInfo extends Record {
    constructor(VLines, HLines, WireMap, LineMap) {
        super();
        this.VLines = VLines;
        this.HLines = HLines;
        this.WireMap = WireMap;
        this.LineMap = LineMap;
    }
}

export function LineInfo_$reflection() {
    return record_type("BusWireRoutingHelpers.LineInfo", [], LineInfo, () => [["VLines", array_type(Line_$reflection())], ["HLines", array_type(Line_$reflection())], ["WireMap", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [int32_type, DrawModelType_BusWireT_Wire_$reflection()])], ["LineMap", class_type("Microsoft.FSharp.Collections.FSharpMap`2", [tuple_type(int32_type, int32_type), int32_type])]]);
}

/**
 * Get the horizontal length of the visible segment emerging from a port
 */
export function getVisibleNubLength(atEnd, wire) {
    const segs = wire.Segments;
    const getLength = (i) => item(atEnd ? ((length(segs) - 1) - i) : i, segs).Length;
    if (getLength(1) < Constants_smallOffset) {
        return getLength(2) + getLength(0);
    }
    else {
        return getLength(0);
    }
}

/**
 * Return true if the segment extends a wire parallel with a nub (wire end).
 */
export function segmentIsNubExtension(wire, segIndex) {
    let this$, this$_1, this$_2, this$_3;
    const segs = wire.Segments;
    const lastSeg = (length(segs) - 1) | 0;
    const revSeg = (n) => item(lastSeg - n, segs);
    const matchValue = (lastSeg - segIndex) | 0;
    let matchResult;
    switch (segIndex) {
        case 0: {
            matchResult = 0;
            break;
        }
        case 2: {
            switch (matchValue) {
                case 0: {
                    matchResult = 0;
                    break;
                }
                case 2: {
                    if ((this$ = item(1, segs), Math.abs(this$.Length) < 1E-07)) {
                        matchResult = 1;
                    }
                    else if ((this$_1 = revSeg(1), Math.abs(this$_1.Length) < 1E-07)) {
                        matchResult = 2;
                    }
                    else {
                        matchResult = 3;
                    }
                    break;
                }
                default:
                    if ((this$_2 = item(1, segs), Math.abs(this$_2.Length) < 1E-07)) {
                        matchResult = 1;
                    }
                    else {
                        matchResult = 3;
                    }
            }
            break;
        }
        default:
            switch (matchValue) {
                case 0: {
                    matchResult = 0;
                    break;
                }
                case 2: {
                    if ((this$_3 = revSeg(1), Math.abs(this$_3.Length) < 1E-07)) {
                        matchResult = 2;
                    }
                    else {
                        matchResult = 3;
                    }
                    break;
                }
                default:
                    matchResult = 3;
            }
    }
    switch (matchResult) {
        case 0:
            return true;
        case 1:
            return true;
        case 2:
            return true;
        default:
            return false;
    }
}

/**
 * Return string to display a wire
 */
export function pWire(wire) {
    return toText(`W${length(wire.Segments)}:${wire.InitialOrientation}->${join("-", map((aSeg) => {
        let matchValue_1, segStart, segEnd, matchValue_2;
        const isMan = (aSeg.Segment.Mode === "auto") ? "A" : "M";
        const left = aSeg.End;
        const right = aSeg.Start;
        new XYPos(left.X - right.X, left.Y - right.Y);
        if (Math.abs(aSeg.Segment.Length) < 1E-07) {
            return isMan + ".S0";
        }
        else {
            return ((isMan + ".") + ((matchValue_1 = ((segStart = aSeg.Start, (segEnd = aSeg.End, (Math.abs(segStart.X - segEnd.X) < 1E-07) ? "vertical" : ((Math.abs(segStart.Y - segEnd.Y) < 1E-07) ? "horizontal" : toFail(printf("ERROR: Diagonal wire")))))), (matchValue_2 = (aSeg.Segment.Length > 0), (matchValue_1 === "horizontal") ? (matchValue_2 ? "Rt" : "Lt") : (matchValue_2 ? "Dn" : "Up"))))) + (`${interpolate("%.0f%P()", [Math.abs(aSeg.Segment.Length)])}`);
        }
    }, getAbsSegments(wire)))}`);
}

/**
 * Return string to display an Option
 */
export function pOpt(x) {
    if (x != null) {
        return `^${value_2(x)}^`;
    }
    else {
        return "None";
    }
}

/**
 * Return string to display the type of a Line
 */
export function pLineType(line) {
    return `${line.LType}`;
}

/**
 * Return string to display a Line
 */
export function pLine(line) {
    return `|${(line.Orientation === "vertical") ? "V" : "H"}L${LineId__get_Index(line.Lid)}.P=${interpolate("%.0f%P()", [line.P])}.${pLineType(line)}:B=${interpolate("%.0f%P()", [line.B.MinB])}-${interpolate("%.0f%P()", [line.B.MaxB])}: (${map((l) => (LineId__get_Index(l.Lid) | 0), line.SameNetLink)})|`;
}

/**
 * Return string to display an array of Lines
 */
export function pLines(lineA) {
    return join("\n", map_1(pLine, lineA));
}

/**
 * Return string to display a Cluster (compactly).
 * See also pAllCluster.
 */
export function pCluster(loc) {
    return `Cluster:<${pOpt(loc.LowerFix)}-${loc.Segments}-${pOpt(loc.UpperFix)}>`;
}

/**
 * Return string to display a Cluster (long form).
 * See also pCluster.
 */
export function pAllCluster(lines, loc) {
    let matchValue;
    return `Cluster-${(matchValue = item_1(0, lines).Orientation, (matchValue === "vertical") ? "Vert" : "Horiz")}:<L=${pOpt(loc.LowerFix)}-${join(",", map((n) => pLine(item_1(n, lines)), loc.Segments))}-U=${pOpt(loc.UpperFix)}>`;
}

/**
 * Linear search in an array from searchStart in a direction dir = +1/-1 from searchStart.
 * Give up and return None if giveUp is true.
 * Return first location for which predicate is true.
 */
export function tryFindIndexInArray(searchStart_mut, dir_mut, predicate_mut, giveUp_mut, arr_mut) {
    tryFindIndexInArray:
    while (true) {
        const searchStart = searchStart_mut, dir = dir_mut, predicate = predicate_mut, giveUp = giveUp_mut, arr = arr_mut;
        if ((LineId__get_Index(searchStart) < 0) ? true : (LineId__get_Index(searchStart) > (arr.length - 1))) {
            return undefined;
        }
        else {
            const matchValue = predicate(item_1(LineId__get_Index(searchStart), arr));
            const matchValue_1 = giveUp(item_1(LineId__get_Index(searchStart), arr));
            if (matchValue_1) {
                return undefined;
            }
            else if (matchValue) {
                return searchStart;
            }
            else {
                searchStart_mut = (LineId__get_Index(searchStart) + dir);
                dir_mut = dir;
                predicate_mut = predicate;
                giveUp_mut = giveUp;
                arr_mut = arr;
                continue tryFindIndexInArray;
            }
        }
        break;
    }
}

/**
 * True if bounds b1 and b2 overlap or are exactly adjacent
 */
export function hasOverlap(b1, b2) {
    let x, x_1;
    if (((x = b2.MinB, ((b1.MinB - Constants_intervalTolerance) < x) && (x < (b1.MaxB + Constants_intervalTolerance)))) ? true : ((x_1 = b2.MaxB, ((b1.MinB - Constants_intervalTolerance) < x_1) && (x_1 < (b1.MinB + Constants_intervalTolerance))))) {
        return true;
    }
    else {
        const x_2 = b1.MinB;
        if ((b2.MinB - Constants_intervalTolerance) < x_2) {
            return x_2 < (b2.MaxB + Constants_intervalTolerance);
        }
        else {
            return false;
        }
    }
}

/**
 * True if bounds b1 and b2 overlap or are exactly adjacent
 */
export function hasNearOverlap(tolerance, b1, b2) {
    let x, b, x_1, b_1;
    if (((x = b2.MinB, (b = (b1.MaxB + tolerance), (((b1.MinB - tolerance) + Constants_intervalTolerance) < x) && (x < (b - Constants_intervalTolerance))))) ? true : ((x_1 = b2.MaxB, (b_1 = (b1.MinB + tolerance), (((b1.MinB - tolerance) + Constants_intervalTolerance) < x_1) && (x_1 < (b_1 - Constants_intervalTolerance)))))) {
        return true;
    }
    else {
        const x_2 = b1.MinB;
        const b_2 = b2.MaxB + tolerance;
        if (((b2.MinB - tolerance) + Constants_intervalTolerance) < x_2) {
            return x_2 < (b_2 - Constants_intervalTolerance);
        }
        else {
            return false;
        }
    }
}

/**
 * Return union of two bounds b1 and b2. b1 & b2 must overlap or be adjacent.
 * Otherwise the inclusive interval containing b1 and b2 is returned.
 */
export function boundUnion(b1, b2) {
    return new Bound(min(b1.MinB, b2.MinB), max(b1.MaxB, b2.MaxB));
}

/**
 * Move segment by amount posDelta in direction perpendicular to segment - + => X or y increases.
 * Movement is by changing lengths of two segments on either side.
 * Will fail if called to change a nub at either end of a wire (nubs cannot move).
 */
export function moveSegment(index, posDelta, wire) {
    let source_1, bind$0040, bind$0040_1;
    const segs = wire.Segments;
    if ((index < 1) ? true : (index > (length(segs) - 2))) {
        toFail(`What? moveSegment is trying to move segment ${index} of a wire length ${length(segs)}`);
    }
    return new DrawModelType_BusWireT_Wire(wire.WId, wire.InputPort, wire.OutputPort, wire.Color, wire.Width, (source_1 = updateAt(index - 1, (bind$0040 = item(index - 1, segs), new DrawModelType_BusWireT_Segment(bind$0040.Index, item(index - 1, segs).Length + posDelta, bind$0040.WireId, bind$0040.IntersectOrJumpList, bind$0040.Draggable, bind$0040.Mode)), segs), updateAt(index + 1, (bind$0040_1 = item(index + 1, segs), new DrawModelType_BusWireT_Segment(bind$0040_1.Index, item(index + 1, segs).Length - posDelta, bind$0040_1.WireId, bind$0040_1.IntersectOrJumpList, bind$0040_1.Draggable, bind$0040_1.Mode)), source_1)), wire.StartPos, wire.InitialOrientation);
}

/**
 * Change wires to move a wire segment represented by line to the given new value of P coordinate.
 * P is X or Y according to ori.
 */
export function moveLine(ori, newP, line, wires) {
    let mapping, posDelta;
    const matchValue = line.Seg1;
    if (matchValue != null) {
        const seg = matchValue;
        const oldP = (ori === "vertical") ? seg.Start.X : seg.Start.Y;
        if (newP !== oldP) {
            return change(seg.Segment.WireId, (mapping = ((posDelta = (newP - oldP), (wire) => moveSegment(seg.Segment.Index, posDelta, wire))), (option) => {
                const option_1 = option;
                return (option_1 != null) ? mapping(option_1) : undefined;
            }), wires);
        }
        else {
            return wires;
        }
    }
    else {
        return toFail(`Can't move Line ${interpolate("%A%P()", [line])} - it is not a segment`);
    }
}

